import React, { MutableRefObject, Suspense } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";
import AdminTrialCart from "@atlassiansox/admin-trial-cart";

import { GlobalNavUserProfile } from "@/components/global-nav/user-profile-new/src";
import WplButton, { ButtonColor } from "@/components/wpl/button/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";

import { useLoggedInUser } from "../../../utils/logged-in-user-provider";

import SearchButton from "./search-button";

export type SearchAndProfileProps = {
    isShowTryNowLink?: boolean;
    ctaButtonColor?: ButtonColor;
    productKey?: ProductKeyTypes;
    isShowSearchButton?: boolean;
    setIsOpenSearch: React.Dispatch<React.SetStateAction<boolean>>;
    searchAndProfileRef: MutableRefObject<HTMLDivElement | null>;
    navigationTrynowCtaText?: string;
    navigationTrynowCtaUrl?: string;
    navigationTrynowCtaOnClick?: () => void;
    isDisplayDivider?: boolean;
};

const tryNowCss = css({
    marginInlineEnd: token("space.100"),
    a: {
        fontWeight: 500,
        borderRadius: "25px 25px 25px 25px"
    }
});

const WacAppSwitcher = React.lazy(() =>
    // without the line ts-ignored below TSC throws this error:
    // "Relative import paths need explicit file extensions in EcmaScript imports when '--moduleResolution' is 'node16' or 'nodenext'. Did you mean '../../../components/global-nav/app-switcher/src/index.js'?"
    // the file of course ends in .tsx, when you try to add that there is a different error:
    // "An import path can only end with a '.tsx' extension when 'allowImportingTsExtensions' is enabled."

    // @ts-ignore
    import("../../../components/global-nav/app-switcher/src/index").then(
        (module) => ({
            default: module.WacAppSwitcher
        })
    )
);

const SearchAndProfile = ({
    isShowTryNowLink = false,
    ctaButtonColor = "blueprimary700",
    productKey = "home",
    isShowSearchButton = true,
    setIsOpenSearch,
    navigationTrynowCtaText,
    navigationTrynowCtaUrl,
    navigationTrynowCtaOnClick,
    searchAndProfileRef,
    isDisplayDivider = true
}: SearchAndProfileProps) => {
    const intl = useIntl();
    /* TODO: Implement setIsUserLoggedIn, user logged in status (in useEffect), once GWP-51 in implemented  */
    const { isUserLoggedIn } = useLoggedInUser();
    const defaultCTAUrl = useLocalizedHref("/try");
    const wplButtonText =
        navigationTrynowCtaText ??
        intl.formatMessage({
            id: "globalnav.v2.try-now-button-label",
            defaultMessage: "Try Now"
        });
    const wplButtonUrl = navigationTrynowCtaOnClick
        ? undefined
        : navigationTrynowCtaUrl ?? defaultCTAUrl;

    return (
        <div
            css={css({ alignSelf: "end", display: "flex" })}
            ref={searchAndProfileRef}
            className="search-and-profile"
        >
            {isShowTryNowLink && (
                <div className="navigation-trynow" css={tryNowCss}>
                    <WplButton
                        text={wplButtonText}
                        url={wplButtonUrl}
                        color={ctaButtonColor}
                        size="small"
                        dataEventProps={{
                            "data-product-key": productKey,
                            "data-event": "clicked",
                            "data-event-component": "linkButton",
                            "data-event-container": "navbar",
                            "data-label": wplButtonText,
                            "data-label-english": "Get it free",
                            "data-track-event": `${productKey}.get-it-free-button-clicked`
                        }}
                        onClick={navigationTrynowCtaOnClick}
                    />
                </div>
            )}
            <div className="navigation-search">
                {isShowSearchButton && (
                    <SearchButton
                        onClick={(e) => {
                            e.preventDefault();
                            setIsOpenSearch(true);
                        }}
                    />
                )}
            </div>
            <ul className="navigation-action">
                <li>
                    {isDisplayDivider ? (
                        <div className="navigation-switcher-divider" />
                    ) : null}
                    {isUserLoggedIn && (
                        <Suspense>
                            <WacAppSwitcher
                                allowJoinSitesProvider={true}
                                appSwitcherLabel={""}
                                appSwitcherIconPrimaryColor={"#42526e"}
                                isUseV2AppSwitcherStyle={true}
                                isDisplayBorderAndHoverEffect={false}
                            />
                        </Suspense>
                    )}
                </li>
                <li>
                    <GlobalNavUserProfile isUseNewLoggedInUserDropdown={true} />
                </li>
                <li className="navigation-cart-button">
                    <AdminTrialCart version={0} fields={{}} />
                </li>
            </ul>
        </div>
    );
};

export default SearchAndProfile;
